import { useEffect, useRef, useState } from 'react';
import { LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DocumentFrameProps } from 'types/documentEditing';
import { getDocumentMessage, postDocumentSession, sendDocumentFocus, sendDocumentHostReady } from 'utils/documentEditing/documentFrame';
import { EditorFrame, EditorStatusAlert } from '../DocumentEditor.styles';

export const DocumentFrame = ({ session, origin, title, takeLaunchSession, overlayOpen = false }: DocumentFrameProps) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const submitted = useRef(false);
  const overlayWasOpen = useRef(false);
  const [status, setStatus] = useState<'loading' | 'ready' | 'slow' | 'failed'>('loading');
  const [hostOverlayOpen, setHostOverlayOpen] = useState(false);

  useEffect(() => {
    const host = frameRef.current?.closest('.MuiModal-root');
    if (!host) return;
    const updateOverlay = () => setHostOverlayOpen(host.getAttribute('aria-hidden') === 'true');
    updateOverlay();
    const observer = new MutationObserver(updateOverlay);
    observer.observe(host, { attributes: true, attributeFilter: ['aria-hidden'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const open = overlayOpen || hostOverlayOpen;
    if (status !== 'ready' || !frameRef.current || open === overlayWasOpen.current) return;
    sendDocumentFocus(frameRef.current, origin, open, session);
    overlayWasOpen.current = open;
  }, [origin, overlayOpen, hostOverlayOpen, session, status]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const receiveMessage = (event: MessageEvent) => {
      if (getDocumentMessage(event, frame, origin) !== 'App_LoadingStatus') return;
      setStatus('ready');
    };
    window.addEventListener('message', receiveMessage);
    const timeout = window.setTimeout(() => setStatus((value) => (value === 'loading' ? 'slow' : value)), 60000);
    if (!submitted.current) {
      const credentials = takeLaunchSession();
      if (credentials) {
        submitted.current = true;
        try {
          postDocumentSession(frame, credentials);
        } catch {
          setStatus('failed');
        }
      } else {
        setStatus('failed');
      }
    }
    return () => {
      window.removeEventListener('message', receiveMessage);
      window.clearTimeout(timeout);
    };
  }, [origin, takeLaunchSession]);

  return (
    <>
      {status === 'loading' && <LinearProgress aria-label={t('user.documentEditor.loading')} />}
      {status === 'slow' && <EditorStatusAlert severity="warning">{t('user.documentEditor.slowLoading')}</EditorStatusAlert>}
      {status === 'failed' && <EditorStatusAlert severity="error">{t('user.documentEditor.unavailable')}</EditorStatusAlert>}
      <EditorFrame
        ref={frameRef}
        name={`onlyoffice-${session.sessionId}`}
        src="about:blank"
        title={title}
        referrerPolicy="no-referrer"
        allow="clipboard-read; clipboard-write; fullscreen"
        allowFullScreen
        onLoad={() => {
          if (frameRef.current) sendDocumentHostReady(frameRef.current, origin);
        }}
      />
    </>
  );
};
